import type {
  GroupFormOption,
  GroupProcedureOption,
  ProfileGroupPayload,
  ResourceShiftAssignment,
  ResourceWorkType,
  WorkSplit,
} from '@/api/resourceCenterApi'
import type { FormCategory, ProcedureType } from '@/types/cgt'

// Assignment groups & the Resources grid (z8nrz7f0vw). Pure rules only, so they
// are unit-tested; the dialogs just render what these return.

// ─── The group form picker (deck slide 5) ────────────────────────────────────
//
// Filters narrow from left to right: a procedure TYPE (or all), then specific
// procedures of it, then form types. Picking a procedure shows ONLY the forms
// in that procedure's sequence.

export interface FormPickerFilter {
  /** null = every procedure type. */
  procedureType: ProcedureType | null
  /** Empty = every procedure of the chosen type (or no procedure filter at all). */
  procedureIds: string[]
  /** Empty = every form type. */
  categories: FormCategory[]
  search: string
  /** Every form, only the ones linked to the group, or only the rest. */
  show: LinkFilter
}

export type LinkFilter = 'all' | 'linked' | 'unlinked'

export const EMPTY_FORM_FILTER: FormPickerFilter = {
  procedureType: null,
  procedureIds: [],
  categories: [],
  search: '',
  show: 'all',
}

/** The procedures the second filter offers: those of the chosen type, by name. */
export function proceduresForType(
  procedures: GroupProcedureOption[],
  type: ProcedureType | null,
): GroupProcedureOption[] {
  return procedures
    .filter(p => type === null || p.type === type)
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** The procedure types that actually have a procedure, in a stable order. */
export function procedureTypesPresent(procedures: GroupProcedureOption[]): ProcedureType[] {
  return [...new Set(procedures.map(p => p.type))].sort()
}

/** The forms the picker lists for a filter, by name. */
export function filterForms(
  forms: GroupFormOption[],
  procedures: GroupProcedureOption[],
  filter: FormPickerFilter,
  /** The forms linked to the group (saved or not) — for the Linked / Not linked view. */
  linked: string[] = [],
): GroupFormOption[] {
  const isLinked = new Set(linked)
  // Which procedures count: the ticked ones, else every one of the chosen type,
  // else no procedure restriction at all.
  let allowed: Set<string> | null = null
  if (filter.procedureIds.length > 0) {
    allowed = new Set(filter.procedureIds)
  } else if (filter.procedureType !== null) {
    allowed = new Set(proceduresForType(procedures, filter.procedureType).map(p => p.familyId))
  }
  const categories = new Set(filter.categories)
  const needle = filter.search.trim().toLowerCase()

  return forms
    .filter(f => allowed === null || f.procedureFamilyIds.some(id => allowed.has(id)))
    .filter(f => categories.size === 0 || categories.has(f.category))
    .filter(f => needle === '' || f.name.toLowerCase().includes(needle))
    .filter(f => filter.show === 'all' || (filter.show === 'linked') === isLinked.has(f.familyId))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** Procedure names a form appears in, for the table's Procedures column. */
export function procedureNames(form: GroupFormOption, procedures: GroupProcedureOption[]): string[] {
  const byId = new Map(procedures.map(p => [p.familyId, p.name]))
  return form.procedureFamilyIds
    .map(id => byId.get(id))
    .filter((n): n is string => !!n)
    .sort((a, b) => a.localeCompare(b))
}

/** The form types present among some forms — only those are worth a filter chip. */
export function categoriesPresent(forms: GroupFormOption[]): FormCategory[] {
  return [...new Set(forms.map(f => f.category))].sort()
}

/** Tick or untick every listed form at once ("select all shown"). */
export function toggleAll(selected: string[], shown: string[], on: boolean): string[] {
  if (on) return [...new Set([...selected, ...shown])]
  const drop = new Set(shown)
  return selected.filter(id => !drop.has(id))
}

/** Same members, order ignored — whether the form list has unsaved changes. */
export function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const s = new Set(a)
  return b.every(x => s.has(x))
}

// ─── A person's groups: one Primary, the rest Secondary ──────────────────────

/** Add or remove a group. The first group a person gets becomes their Primary. */
export function toggleMembership(
  groups: ProfileGroupPayload[],
  groupId: string,
  on: boolean,
): ProfileGroupPayload[] {
  if (!on) {
    const rest = groups.filter(g => g.groupId !== groupId)
    // Removing the Primary promotes the next one, so a person with groups
    // always has a Team.
    if (rest.length > 0 && !rest.some(g => g.isPrimary)) rest[0] = { ...rest[0], isPrimary: true }
    return rest
  }
  if (groups.some(g => g.groupId === groupId)) return groups
  return [...groups, { groupId, isPrimary: groups.length === 0 }]
}

/** Make one group the Primary; every other becomes Secondary. */
export function setPrimary(groups: ProfileGroupPayload[], groupId: string): ProfileGroupPayload[] {
  return groups.map(g => ({ ...g, isPrimary: g.groupId === groupId }))
}

/** Same groups and same Primary, order ignored. */
export function sameMemberships(a: ProfileGroupPayload[], b: ProfileGroupPayload[]): boolean {
  if (a.length !== b.length) return false
  const byId = new Map(a.map(g => [g.groupId, g.isPrimary]))
  return b.every(g => byId.get(g.groupId) === g.isPrimary)
}

// ─── The split and work-type chips (deck slide 7) ────────────────────────────

export const WORK_TYPE_CHIPS: Record<ResourceWorkType, { label: string; title: string; className: string }> = {
  PROCESS: { label: 'PR', title: 'Process', className: 'bg-blue-50 text-blue-700' },
  REVIEW: { label: 'RV', title: 'Review', className: 'bg-teal-50 text-teal-700' },
  QA: { label: 'QA', title: 'QA', className: 'bg-rose-50 text-rose-700' },
  SUPPORT: { label: 'SA', title: 'Support', className: 'bg-amber-50 text-amber-700' },
}

export interface SplitSegment {
  type: ResourceWorkType
  pct: number
}

/** The bar's segments, in PR·RV·QA·SA order, zero parts left out. Dynamic has none. */
export function splitSegments(split: WorkSplit | null): SplitSegment[] {
  if (!split || split.isDynamic) return []
  const parts: SplitSegment[] = [
    { type: 'PROCESS', pct: split.processPct },
    { type: 'REVIEW', pct: split.reviewPct },
    { type: 'QA', pct: split.qaPct },
    { type: 'SUPPORT', pct: split.supportPct },
  ]
  return parts.filter(p => p.pct > 0)
}

// ─── The shift column ────────────────────────────────────────────────────────

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DAY_LETTER = ['Su', 'M', 'Tu', 'W', 'Th', 'F', 'Sa']

/** "M–F" for a run of three or more days, else "Mon, Wed, Fri". 0 = Sunday. */
export function weekdaysLabel(weekdays: number[]): string {
  const days = [...new Set(weekdays)].sort((a, b) => a - b)
  if (days.length === 0) return 'no days'
  if (days.length === 7) return 'every day'
  const isRun = days.every((d, i) => i === 0 || d === days[i - 1] + 1)
  if (isRun && days.length >= 3) return `${DAY_LETTER[days[0]]}–${DAY_LETTER[days[days.length - 1]]}`
  return days.map(d => DAY_SHORT[d]).join(', ')
}

/** "10/1" from "2026-10-01" — how the deck writes a start date. */
function shortDate(iso: string): string {
  const [, m, d] = iso.split('-').map(Number)
  return `${m}/${d}`
}

/** The two lines of the Shift column, as on slide 7. */
export function shiftSummary(shift: ResourceShiftAssignment | null): { title: string; detail: string } | null {
  if (!shift) return null
  if (shift.type === 'FIXED') {
    const name = shift.shift?.name ?? 'Shift'
    const times = shift.shift ? ` · ${shift.shift.startTime}–${shift.shift.endTime}` : ''
    return { title: `${name} (${weekdaysLabel(shift.weekdays)})`, detail: `Fixed${times}` }
  }
  if (shift.type === 'PATTERN') {
    return {
      title: shift.pattern?.name ?? 'Pattern',
      detail: `Pattern · from ${shortDate(shift.effectiveFrom)}`,
    }
  }
  return { title: 'Variable', detail: 'Varies by day' }
}

/** "60 · 30 · 10", "Dynamic", or null when no split is set. */
export function splitLabel(split: WorkSplit | null): string | null {
  if (!split) return null
  if (split.isDynamic) return 'Dynamic'
  return splitSegments(split)
    .map(s => s.pct)
    .join(' · ')
}
